/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useUser } from '../hooks/UNSAFE_useUser';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { classNames } from '../utils/UNSAFE_classNames';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { ColorProps } from '../utils/UNSAFE_interpolations/colors';

type IntrinsicProps = Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'class'>;
type ThresholdBarProps = IntrinsicProps & {
  color?: ColorProps['color'];
  length: string;
  orientation: 'horizontal' | 'vertical';
};

export function ThresholdBar({ color, length, orientation, ...otherProps }: ThresholdBarProps) {
  const { direction } = useUser();
  const isHoriz = orientation === 'horizontal';
  const isRtl = direction === 'rtl';
  return (
    <div
      class={classNames([
        styles.base,
        isHoriz
          ? styles[`horizontalBarTrackSection${isRtl ? 'Rtl' : 'Ltr'}`]
          : styles['verticalBarTrackSection'],
        otherProps.class
      ])}
      style={{
        width: isHoriz ? length : '100%',
        height: isHoriz ? '100%' : length,
        backgroundColor: color
      }}></div>
  );
}

const styles = {
  base: cssProps`${{
    borderColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-dvt-contrast-line-color)',
    borderStyle: 'solid',
    boxSizing: 'border-box',
    pointerEvents: 'none',
    backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-threshold-color)',
    backgroundClip: 'padding-box'
  }}`,
  verticalBarTrackSection: cssProps`${{
    borderWidth: '1px 0 0 0'
  }}`,
  horizontalBarTrackSectionRtl: cssProps`${{
    borderWidth: '0 0 0 1px'
  }}`,
  horizontalBarTrackSectionLtr: cssProps`${{
    borderWidth: '0 1px 0 0'
  }}`
};
